import { Suspense, lazy } from 'react'
import { BrowserRouter, Route, Routes } from 'react-router-dom'
import AppLayout from './app/AppLayout'
import DocumentTitle from './app/DocumentTitle'
import KitPage from './app/KitPage'
import LegacyRedirects from './app/LegacyRedirects'
import PlaceholderPage from './app/PlaceholderPage'
import Landing from './app/Landing'
import SessionGate from './app/SessionGate'
import SessionRevalidator from './app/SessionRevalidator'
import { adminMenu } from './app/navigation'
import PageLoader from './components/PageLoader'
import EnvironmentBadge from './components/EnvironmentBadge'
import { SessionProvider } from './lib/session'

/** The dashboard pulls in ECharts; keeping it out of the entry chunk halves it. */
const GeneralPage = lazy(() => import('./features/dashboard/GeneralPage'))
/* Its own chunk: it is opened in a new tab from the dashboard, never on the way to it. */
const StatisticsReportPage = lazy(() => import('./features/dashboard/StatisticsReportPage'))
import InstallPage from './features/install/InstallPage'
import LoginPage from './features/login/LoginPage'
import OtpPage from './features/login/OtpPage'
import ResetPasswordPage from './features/login/ResetPasswordPage'
import ActivatePage from './features/signup/ActivatePage'
import GroupsPage from './features/groups/GroupsPage'
import LicensePage from './features/license/LicensePage'
import CategoriesPage from './features/content/CategoriesPage'
import CategoryFormPage from './features/content/CategoryFormPage'
import AssessmentReportPage from './features/engagement/AssessmentReportPage'
import AssessmentsPage from './features/engagement/AssessmentsPage'
import CertificateValidationPage from './features/incidents/CertificateValidationPage'
import IncidentsPage from './features/incidents/IncidentsPage'
import ReportPage from './features/incidents/ReportPage'
import AnalysisPage from './features/gamification/AnalysisPage'
import GamificationSettingsPage from './features/gamification/GamificationSettingsPage'
import PointsLogPage from './features/gamification/PointsLogPage'
import CampaignsPage from './features/phishing/CampaignsPage'
import PhishingReportPage from './features/phishing/PhishingReportPage'
import PhishingSettingsPage from './features/phishing/PhishingSettingsPage'
import TemplateFormPage from './features/phishing/TemplateFormPage'
import TemplatesPage from './features/phishing/TemplatesPage'
import AwarenessFormPage from './features/engagement/AwarenessFormPage'
import AwarenessPage from './features/engagement/AwarenessPage'
import PoliciesPage from './features/engagement/PoliciesPage'
import PolicyFormPage from './features/engagement/PolicyFormPage'
import SurveyFormPage from './features/engagement/SurveyFormPage'
import SurveysPage from './features/engagement/SurveysPage'
import CertificatesPage from './features/content/CertificatesPage'
import QuestionsPage from './features/content/QuestionsPage'
import ProgramDetailPage from './features/programs/ProgramDetailPage'
import CourseFormPage from './features/programs/CourseFormPage'
import ProgramFormPage from './features/programs/ProgramFormPage'
import ProgramsPage from './features/programs/ProgramsPage'
import QuickProgramPage from './features/programs/QuickProgramPage'
import ExamsPage from './features/exams/ExamsPage'
import PrintCertificatePage from './features/certificates/PrintCertificatePage'
import ProgramReportsPage from './features/programs/ProgramReportsPage'
import ProfilePage from './features/account/ProfilePage'
import RedirectPage from './features/account/RedirectPage'
import SystemPage from './features/account/SystemPage'
import InvitationsPage from './features/invitations/InvitationsPage'
import CompaniesPage from './features/organisations/CompaniesPage'
import CompanyProfilePage from './features/organisations/CompanyProfilePage'
import LicenseInformationPage from './features/organisations/LicenseInformationPage'
import LicenseManagementPage from './features/organisations/LicenseManagementPage'
import AdvancedSettingsPage from './features/settings/AdvancedSettingsPage'
import SettingsKeysPage from './features/settings/SettingsKeysPage'
import SettingsPage from './features/settings/SettingsPage'
import NotificationsLogPage from './features/systemLogs/NotificationsLogPage'
import SystemStatePage from './features/systemLogs/SystemStatePage'
import SignupPage from './features/signup/SignupPage'
import UserDetailsPage from './features/userDetails/UserDetailsPage'
import UsersPage from './features/users/UsersPage'
/** The learner's panel — its own shell and routes, under /portal. */
const UserApp = lazy(() => import('./user/UserApp'))

/** Every side-menu destination, so nothing in the rail dead-ends. */
const menuRoutes = adminMenu.flatMap((group) => group.items)

export default function App() {
  return (
    <BrowserRouter>
      <EnvironmentBadge />
      {/* Who is signed in, once, above the router — the rail, the header, the menu
          guards and the force-redirection gate all read the same bootstrap. */}
      <SessionProvider>
        {/* Above <Routes>: below it, the catch-all would claim a legacy URL first
            and send a live bookmark to the login page. */}
        <LegacyRedirects>
          {/* Names the browser tab after whatever route is showing. Inside the
              redirects, so a legacy bookmark is titled by where it LANDS. */}
          <DocumentTitle />
          {/* Re-reads the session on navigation, so a gate that appears while the tab is
              open is acted on. Here rather than in `SessionGate` — that one is mounted
              three times and remounts between the admin, portal and report branches. */}
          <SessionRevalidator />
          <Routes>
            {/* `/` is decided by the session, not by the catch-all. See `Landing`. */}
            <Route path="/" element={<Landing />} />

            <Route path="/login" element={<LoginPage />} />
            <Route path="/login/otp" element={<OtpPage />} />
            <Route path="/signup" element={<SignupPage />} />
            {/* Reached from a password-reset email, carrying username and token. */}
            <Route path="/reset-password" element={<ResetPasswordPage />} />
            {/* Reached from email — every one of these URLs is live in the wild. */}
            {/* The activation link itself: opening it confirms the address. */}
            <Route path="/activate" element={<ActivatePage state="confirm" />} />
            <Route path="/activate/waiting-user" element={<ActivatePage state="waitingUser" />} />
            <Route path="/activate/waiting-admin" element={<ActivatePage state="waitingAdmin" />} />
            <Route path="/activate/expired" element={<ActivatePage state="expired" />} />
            <Route path="/activate/resigned" element={<ActivatePage state="resigned" />} />
            <Route path="/activate/suspended" element={<ActivatePage state="suspended" />} />
            <Route path="/install" element={<InstallPage />} />
            {/* Standalone surfaces — the legacy renders these outside the admin shell. */}
            <Route path="/error" element={<SystemPage state="error" />} />
            {/* The server words the message differently per status code. */}
            <Route path="/error/:statusCode" element={<SystemPage state="error" />} />
            <Route path="/unauthorized" element={<SystemPage state="unauthorized" />} />
            <Route path="/shutdown" element={<SystemPage state="shutdown" />} />
            {/* The first-sign-in interstitial for an incomplete profile. */}
            <Route path="/redirect" element={<RedirectPage />} />
    
            {/* الإحصائيات العامة on their own page — the board the fullscreen button opens in
                a new tab, and the document the PDF button prints. Outside `AppLayout` for the
                same reason the learner panel is: it has no sidebar and no toolbar, which is
                exactly what `_AdminsReportBaseLayout` does for the legacy's `Report.cshtml`. */}
            <Route
              path="/general/report"
              element={
                <SessionGate>
                  <Suspense fallback={<PageLoader full />}>
                    <StatisticsReportPage />
                  </Suspense>
                </SessionGate>
              }
            />

            {/* The learner's panel. Its own shell, so it sits outside AppLayout —
                which is also why the fallback is `full`: the shell is in this chunk
                too, so until it lands there is no frame for the loader to sit in. */}
            <Route
              path="/portal/*"
              element={
                <SessionGate>
                  <Suspense fallback={<PageLoader full />}>
                    <UserApp />
                  </Suspense>
                </SessionGate>
              }
            />
    
            <Route
              element={
                <SessionGate>
                  <AppLayout />
                </SessionGate>
              }
            >
              <Route
                path="/general"
                element={
                  <Suspense fallback={<PageLoader />}>
                    <GeneralPage />
                  </Suspense>
                }
              />
              <Route path="/license" element={<LicensePage />} />
              <Route path="/programs" element={<ProgramsPage />} />
              {/* Forms are routes, not sheets: the browser back button then means
                  the same thing as the form's own back arrow. */}
              <Route path="/programs/new" element={<ProgramFormPage />} />
              <Route path="/programs/quick" element={<QuickProgramPage />} />
              <Route path="/exams" element={<ExamsPage />} />
              <Route path="/print-certificate" element={<PrintCertificatePage />} />
              <Route path="/programs-reports" element={<ProgramReportsPage />} />
              <Route path="/programs/:id/edit" element={<ProgramFormPage />} />
              <Route path="/programs/:id/courses/new" element={<CourseFormPage />} />
              <Route path="/programs/:id/courses/:courseId/edit" element={<CourseFormPage />} />
              <Route path="/programs/:id" element={<ProgramDetailPage />} />
              <Route path="/categories" element={<CategoriesPage />} />
              <Route path="/categories/new" element={<CategoryFormPage />} />
              <Route path="/categories/:id/edit" element={<CategoryFormPage />} />
              <Route path="/questions" element={<QuestionsPage />} />
              <Route path="/certificates" element={<CertificatesPage />} />
              <Route path="/policies" element={<PoliciesPage />} />
              <Route path="/policies/new" element={<PolicyFormPage />} />
              <Route path="/policies/:id/edit" element={<PolicyFormPage />} />
              <Route path="/awareness" element={<AwarenessPage />} />
              <Route path="/awareness/new" element={<AwarenessFormPage />} />
              <Route path="/awareness/:id/edit" element={<AwarenessFormPage />} />
              <Route path="/assessments-management" element={<AssessmentsPage />} />
              <Route
                path="/assessments-management/:id/report"
                element={<AssessmentReportPage />}
              />
              <Route path="/surveys-management" element={<SurveysPage />} />
              <Route path="/surveys-management/new" element={<SurveyFormPage />} />
              <Route path="/surveys-management/:id/edit" element={<SurveyFormPage />} />
              <Route path="/phishing/templates" element={<TemplatesPage />} />
              <Route path="/phishing/templates/new" element={<TemplateFormPage />} />
              <Route path="/phishing/templates/:id/edit" element={<TemplateFormPage />} />
              <Route path="/phishing/campaigns" element={<CampaignsPage />} />
              <Route path="/phishing/settings" element={<PhishingSettingsPage />} />
              {/* One component, two scopes — the whole programme, or one campaign. */}
              <Route path="/phishing/report" element={<PhishingReportPage />} />
              <Route path="/phishing/report/:campaignId" element={<PhishingReportPage />} />
              {/* One page, two tabs — both legacy URLs still resolve (§6.7). */}
              <Route path="/gamification/transactions" element={<PointsLogPage tab="transactions" />} />
              <Route path="/gamification/reset-logs" element={<PointsLogPage tab="resetLogs" />} />
              <Route path="/gamification/analysis" element={<AnalysisPage />} />
              <Route path="/gamification/settings" element={<GamificationSettingsPage />} />
              <Route path="/security-reports" element={<IncidentsPage />} />
              <Route path="/security-reports/:id" element={<ReportPage />} />
              <Route path="/certificate-validation" element={<CertificateValidationPage />} />
              <Route path="/users" element={<UsersPage />} />
              <Route path="/users/:id" element={<UserDetailsPage />} />
              <Route path="/groups" element={<GroupsPage />} />
              <Route path="/invitations" element={<InvitationsPage />} />
              <Route path="/settings" element={<SettingsPage />} />
              <Route path="/settings/advanced" element={<AdvancedSettingsPage />} />
              <Route path="/settings/keys" element={<SettingsKeysPage />} />
              <Route path="/system-state" element={<SystemStatePage />} />
              <Route path="/notifications-log" element={<NotificationsLogPage />} />
              <Route path="/companies" element={<CompaniesPage />} />
              <Route path="/companies/license" element={<LicenseManagementPage />} />
              <Route path="/companies/:id/profile" element={<CompanyProfilePage />} />
              {/* No menu entry in the legacy either — reached by typing the URL. */}
              <Route path="/license-information" element={<LicenseInformationPage />} />
              {menuRoutes
                .filter(
                  (item) =>
                    ![
                      '/general',
                      '/license',
                      '/programs',
                      '/categories',
                      '/questions',
                      '/certificates',
                      '/policies',
                      '/awareness',
                      '/assessments-management',
                      '/surveys-management',
                      '/phishing/templates',
                      '/phishing/campaigns',
                      '/phishing/settings',
                      '/gamification/transactions',
                      '/gamification/reset-logs',
                      '/gamification/analysis',
                      '/gamification/settings',
                      '/security-reports',
                      '/certificate-validation',
                      '/users',
                      '/groups',
                      '/invitations',
                    ].includes(item.to),
                )
                .map((item) => (
                  <Route key={item.id} path={item.to} element={<PlaceholderPage />} />
                ))}
              <Route path="/profile" element={<ProfilePage />} />
              {/* Development preview of the shared kit; not linked from the side menu. */}
              <Route path="/_kit" element={<KitPage />} />
            </Route>

            {/* An address that matches nothing lands wherever this visitor belongs, for the
                same reason the root does: sending a signed-in user to a sign-in form is
                wrong whichever URL they mistyped to get there. */}
            <Route path="*" element={<Landing />} />
          </Routes>
        </LegacyRedirects>
      </SessionProvider>
    </BrowserRouter>
  )
}
